import { useMemo, useState } from 'react';

// Step between the total quantities offered for a delivery term, per cargo
// type (1 Full Cargo VLGC, 2 Half Cargo VLGC, 3 Semi Refrigerated), used when
// the procurement leaves the step empty. Matches the earlier fixed steps.
export const DEFAULT_QUANTITY_STEP = { 1: 1000, 2: 500, 3: 500 };

const QUANTITY_STEP_FIELD = {
  1: 'fullCargoVLGCQuantityStep',
  2: 'halfCargoVLGCQuantityStep',
  3: 'semiRefrigeratedQuantityStep',
};

export const quantityStepFor = (form, cargoType) => {
  const value = Number(form[QUANTITY_STEP_FIELD[cargoType]]);
  return value > 0 ? value : DEFAULT_QUANTITY_STEP[cargoType];
};

// Interval of the single-component LPG types, set on their Composition. Empty
// keeps the earlier fixed 500.
export const DEFAULT_COMPOSITION_QUANTITY_STEP = 500;

export const compositionQuantityStepFor = form => {
  const value = Number(form?.compositionQuantityStep);
  return value > 0 ? value : DEFAULT_COMPOSITION_QUANTITY_STEP;
};

const FREQUENCY_FIELD = {
  1: 'fullCargoVLGCFrequency',
  2: 'halfCargoVLGCFrequency',
  3: 'semiRefrigeratedFrequency',
};

// Frequency set in the Composition section for one cargo type.
export const frequencyFor = (form, cargoType) =>
  form?.[FREQUENCY_FIELD[Number(cargoType)]] ?? null;

export function flatToCards(flat) {
  return [
    {
      id: 'full-vlgc',
      title: 'Full Cargo VLGC',
      enabled: flat.isFullCargoVLGC ?? false,
      step: flat.isFullCargoVLGC
        ? (flat.fullCargoVLGCQuantityStep ?? null)
        : null,
      defaultStep: DEFAULT_QUANTITY_STEP[1],
      frequency: flat.isFullCargoVLGC
        ? (flat.fullCargoVLGCFrequency ?? '')
        : null,
      propane: {
        min: flat.isFullCargoVLGC
          ? (flat.fullCargoVLGCRefrigratePropaneMin ?? '')
          : null,
        max: flat.isFullCargoVLGC
          ? (flat.fullCargoVLGCRefrigratePropaneMax ?? '')
          : null,
      },
      butane: {
        min: flat.isFullCargoVLGC
          ? (flat.fullCargoVLGCRefrigrateButaneMin ?? '')
          : null,
        max: flat.isFullCargoVLGC
          ? (flat.fullCargoVLGCRefrigrateButaneMax ?? '')
          : null,
      },
    },
    {
      id: 'half-vlgc',
      title: 'Half Cargo VLGC',
      enabled: flat.isHalfCargoVLGC ?? false,
      step: flat.isHalfCargoVLGC
        ? (flat.halfCargoVLGCQuantityStep ?? null)
        : null,
      defaultStep: DEFAULT_QUANTITY_STEP[2],
      frequency: flat.isHalfCargoVLGC
        ? (flat.halfCargoVLGCFrequency ?? null)
        : null,
      propane: {
        min: flat.isHalfCargoVLGC
          ? (flat.halfCargoVLGCRefrigratePropaneMin ?? null)
          : null,
        max: flat.isHalfCargoVLGC
          ? (flat.halfCargoVLGCRefrigratePropaneMax ?? null)
          : null,
      },
      butane: {
        min: flat.isHalfCargoVLGC
          ? (flat.halfCargoVLGCRefrigrateButaneMin ?? null)
          : null,
        max: flat.isHalfCargoVLGC
          ? (flat.halfCargoVLGCRefrigrateButaneMax ?? null)
          : null,
      },
    },
    {
      id: 'semi-refregerated',
      title: 'Semi Refrigerated',
      enabled: flat.isSemiRefrigerated ?? false,
      step: flat.isSemiRefrigerated
        ? (flat.semiRefrigeratedQuantityStep ?? null)
        : null,
      defaultStep: DEFAULT_QUANTITY_STEP[3],
      frequency: flat.isSemiRefrigerated
        ? (flat.semiRefrigeratedFrequency ?? null)
        : null,
      propane: {
        min: flat.isSemiRefrigerated
          ? (flat.semiRefrigeratedRefrigratePropaneMin ?? null)
          : null,
        max: flat.isSemiRefrigerated
          ? (flat.semiRefrigeratedRefrigratePropaneMax ?? null)
          : null,
      },
      butane: {
        min: flat.isSemiRefrigerated
          ? (flat.semiRefrigeratedRefrigrateButaneMin ?? null)
          : null,
        max: flat.isSemiRefrigerated
          ? (flat.semiRefrigeratedRefrigrateButaneMax ?? null)
          : null,
      },
    },
  ];
}

// Field-name prefix per card, so input ids match the validation error keys
// (e.g. fullCargoVLGCRefrigratePropaneMin) and a failed save can focus them.
const CARD_FIELD_PREFIX = {
  'full-vlgc': 'fullCargoVLGC',
  'half-vlgc': 'halfCargoVLGC',
  'semi-refregerated': 'semiRefrigerated',
};

export function cardsToFlat(cards) {
  const find = id => cards.find(c => c.id === id) ?? {};
  const full = find('full-vlgc');
  const half = find('half-vlgc');
  const semi = find('semi-refregerated');

  return {
    isFullCargoVLGC: full.enabled ?? false,
    fullCargoVLGCFrequency: full.frequency ?? null,
    fullCargoVLGCRefrigratePropaneMin: full.propane?.min ?? null,
    fullCargoVLGCRefrigratePropaneMax: full.propane?.max ?? null,
    fullCargoVLGCRefrigrateButaneMin: full.butane?.min ?? null,
    fullCargoVLGCRefrigrateButaneMax: full.butane?.max ?? null,
    isHalfCargoVLGC: half.enabled ?? false,
    halfCargoVLGCFrequency: half.frequency ?? null,
    halfCargoVLGCRefrigratePropaneMin: half.propane?.min ?? null,
    halfCargoVLGCRefrigratePropaneMax: half.propane?.max ?? null,
    halfCargoVLGCRefrigrateButaneMin: half.butane?.min ?? null,
    halfCargoVLGCRefrigrateButaneMax: half.butane?.max ?? null,
    isSemiRefrigerated: semi.enabled ?? false,
    semiRefrigeratedFrequency: semi.frequency ?? null,
    semiRefrigeratedRefrigratePropaneMin: semi.propane?.min ?? null,
    semiRefrigeratedRefrigratePropaneMax: semi.propane?.max ?? null,
    semiRefrigeratedRefrigrateButaneMin: semi.butane?.min ?? null,
    semiRefrigeratedRefrigrateButaneMax: semi.butane?.max ?? null,
    fullCargoVLGCQuantityStep: full.step ?? null,
    halfCargoVLGCQuantityStep: half.step ?? null,
    semiRefrigeratedQuantityStep: semi.step ?? null,
  };
}

function QuantityStepField({ id, value, placeholder, onChange, disabled }) {
  return (
    <div
      className='d-flex align-items-center border rounded bg-white ms-auto'
      style={{ height: 32, width: 170, opacity: disabled ? 0.5 : 1 }}
      title='Quantity interval: each delivery quantity option goes up by this amount'
    >
      <label htmlFor={id} className='small text-muted ps-2 pe-1 mb-0'>
        Interval
      </label>
      <input
        id={id}
        type='number'
        min={0}
        step='any'
        placeholder={String(placeholder)}
        aria-label='Quantity interval'
        value={value ?? ''}
        onChange={
          disabled
            ? undefined
            : e =>
                onChange(
                  e.target.value === ''
                    ? null
                    : Math.max(0, Number(e.target.value))
                )
        }
        disabled={disabled}
        className='form-control border-0 bg-transparent shadow-none px-1 text-end'
        style={{ minWidth: 0, flex: '1 1 0%', height: '100%' }}
      />
      <span className='small text-muted pe-2 ps-1 flex-shrink-0'>MT</span>
    </div>
  );
}

function Checkbox({ checked, onChange, disabled }) {
  return (
    <button
      type='button'
      onClick={disabled ? undefined : onChange}
      disabled={disabled}
      aria-pressed={checked}
      className={`btn p-0 d-inline-flex align-items-center justify-content-center rounded-circle border ${
        checked ? 'bg-secondary border-secondary text-white' : 'bg-white border'
      }`}
      style={{
        width: 20,
        height: 20,
        minWidth: 20,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {checked && <i className='ri-check-line fs-14' />}
    </button>
  );
}

function Stepper({ value, onChange, min = 0, disabled }) {
  return (
    <input
      type='number'
      min={min}
      placeholder='0'
      aria-label='Frequency'
      value={value ?? ''}
      onChange={
        disabled
          ? undefined
          : e => {
              const v = e.target.value;
              onChange(v === '' ? min : Math.max(min, Number(v)));
            }
      }
      disabled={disabled}
      className='form-control form-control-sm text-center'
      style={{ width: 90, height: 36, opacity: disabled ? 0.5 : 1 }}
    />
  );
}

function NumberField({ id, label, value, onChange, unit = 'MT', disabled }) {
  return (
    <div
      className='d-flex align-items-center border rounded bg-white'
      style={{ height: 36 }}
    >
      <span className='small text-muted ps-2 pe-1'>{label}</span>
      <input
        id={id}
        type='number'
        step='0.001'
        placeholder='0.000'
        aria-label={label}
        value={value}
        onChange={
          disabled
            ? undefined
            : e => onChange(e.target.value === '' ? '' : Number(e.target.value))
        }
        disabled={disabled}
        className='form-control border-0 bg-transparent shadow-none px-1'
        style={{
          minWidth: 0,
          flex: '1 1 0%',
          height: '100%',
          MozAppearance: 'textfield',
          WebkitAppearance: 'textfield',
          opacity: disabled ? 0.5 : 1,
        }}
      />
      <span className='small text-muted pe-2 ps-1 flex-shrink-0'>{unit}</span>
    </div>
  );
}

function RefrigerateGroup({
  idPrefix,
  title,
  data,
  onChange,
  error,
  disabled,
}) {
  return (
    <div className='flex-fill border rounded p-3 bg-white'>
      <div className='small fw-medium text-muted mb-2'>{title}</div>
      <div className='d-flex flex-column gap-2'>
        <NumberField
          id={`${idPrefix}Min`}
          label='Min'
          value={data.min}
          onChange={v => onChange({ ...data, min: v })}
          disabled={disabled}
        />
        <NumberField
          id={`${idPrefix}Max`}
          label='Max'
          value={data.max}
          onChange={v => onChange({ ...data, max: v })}
          disabled={disabled}
        />
      </div>
      {error && (
        <div className='text-danger d-flex align-items-center gap-1 mt-1'>
          <i className='ri-error-warning-line'></i>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function CargoCard({
  card,
  onChange,
  groupErrors,
  comptitle,
  sectitle,
  isProcurementCompleted,
  qtyDeliveryData,
  idPrefix,
}) {
  const update = patch => onChange({ ...card, ...patch });

  return (
    <div
      className='border rounded bg-light p-3'
      style={{ minWidth: 260, flex: '1 1 0%' }}
    >
      <div className='d-flex align-items-center gap-2 mb-3'>
        <Checkbox
          checked={card.enabled}
          onChange={() => update({ enabled: !card.enabled })}
          disabled={
            isProcurementCompleted !== false ||
            qtyDeliveryData?.items?.length > 0
          }
        />
        <span className='small fw-semibold text-dark'>{card.title}</span>
        <QuantityStepField
          id={`${idPrefix}${CARD_FIELD_PREFIX[card.id]}QuantityStep`}
          value={card.step}
          placeholder={card.defaultStep}
          onChange={v => update({ step: v })}
          disabled={
            !card.enabled ||
            isProcurementCompleted !== false ||
            qtyDeliveryData?.items?.length > 0
          }
        />
      </div>

      <div
        className={`d-flex flex-column gap-2 ${
          card.enabled ? '' : 'pe-none opacity-50'
        }`}
      >
        <div className='d-flex align-items-center justify-content-between border rounded bg-white px-3 py-2'>
          <span className='small text-muted'>Frequency</span>
          <Stepper
            value={card.frequency}
            onChange={v => update({ frequency: v })}
            disabled={
              isProcurementCompleted !== false ||
              qtyDeliveryData?.items?.length > 0
            }
          />
        </div>

        <div className='d-flex gap-2'>
          <RefrigerateGroup
            idPrefix={`${idPrefix}${CARD_FIELD_PREFIX[card.id]}RefrigratePropane`}
            title={comptitle}
            data={card.propane}
            onChange={propane => update({ propane })}
            error={groupErrors[`${card.id}-propane`]}
            disabled={
              isProcurementCompleted !== false ||
              qtyDeliveryData?.items?.length > 0
            }
          />
          <RefrigerateGroup
            idPrefix={`${idPrefix}${CARD_FIELD_PREFIX[card.id]}RefrigrateButane`}
            title={sectitle}
            data={card.butane}
            onChange={butane => update({ butane })}
            error={groupErrors[`${card.id}-butane`]}
            disabled={
              isProcurementCompleted !== false ||
              qtyDeliveryData?.items?.length > 0
            }
          />
        </div>
      </div>
    </div>
  );
}

export default function CargoTypeComposition({
  form,
  setForm,
  comptitle,
  sectitle,
  qtyDeliveryData,
  idPrefix = '',
}) {
  const cards = useMemo(() => flatToCards(form), [form]);
  const [groupErrors, setGroupErrors] = useState({});

  const validateGroup = (cardId, groupType, min, max) => {
    const key = `${cardId}-${groupType}`;
    const minVal = min === '' || min == null ? null : Number(min);
    const maxVal = max === '' || max == null ? null : Number(max);

    if (minVal != null && maxVal != null && minVal > maxVal) {
      setGroupErrors(prev => ({
        ...prev,
        [key]: 'Min cannot be greater than Max',
      }));
    } else {
      setGroupErrors(prev => {
        // eslint-disable-next-line no-unused-vars
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const updateCard = updated => {
    if (updated.propane) {
      validateGroup(
        updated.id,
        'propane',
        updated.propane.min,
        updated.propane.max
      );
    }
    if (updated.butane) {
      validateGroup(
        updated.id,
        'butane',
        updated.butane.min,
        updated.butane.max
      );
    }

    const reset =
      updated.enabled === false
        ? {
            frequency: null,
            step: null,
            propane: { min: null, max: null },
            butane: { min: null, max: null },
          }
        : {};
    const finalCard = { ...updated, ...reset };
    const newCards = cards.map(c => (c.id === finalCard.id ? finalCard : c));
    const flat = cardsToFlat(newCards);
    setForm(prev => ({ ...prev, ...flat }));
  };

  return (
    <div>
      <div>
        <div className='d-flex flex-row flex-nowrap gap-3 overflow-auto pb-2'>
          {cards.map(card => (
            <CargoCard
              key={card.id}
              card={card}
              onChange={updateCard}
              groupErrors={groupErrors}
              comptitle={comptitle || sectitle}
              sectitle={sectitle}
              isProcurementCompleted={form.isProcurementCompleted}
              qtyDeliveryData={qtyDeliveryData}
              idPrefix={idPrefix}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
